import type { CallAnalysisComplianceCheck } from '@marnic/shared';
import { MiniSection } from '../primitives/MiniSection';
import { RiskBadge } from '../primitives/RiskBadge';

export function ComplianceCard({ compliance }: { compliance: CallAnalysisComplianceCheck | null }): JSX.Element | null {
  if (!compliance) return null;
  if (compliance.skipped) {
    return (
      <MiniSection title="Compliance">
        <p className="text-[11px] text-ink-500 italic">
          Compliance pass skipped — no rules configured.
        </p>
      </MiniSection>
    );
  }

  const violations = compliance.violations ?? [];
  const legacyForbidden = compliance.forbiddenPhrasesDetected ?? [];
  const legacyClaims = compliance.regulatedClaims ?? [];
  const notes = Array.isArray(compliance.notes)
    ? compliance.notes
    : compliance.notes
      ? [compliance.notes]
      : [];

  return (
    <MiniSection title="Compliance">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="text-ink-500">Risk:</span>
        <RiskBadge severity={compliance.riskLevel} />
        {compliance.disclosureMade !== null && compliance.disclosureMade !== undefined && (
          <span className="rounded bg-white px-2 py-0.5">
            Disclosure: {compliance.disclosureMade ? 'yes' : 'no'}
          </span>
        )}
        {compliance.incidentRecommended && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-red-800 font-medium">
            Incident recommended
          </span>
        )}
        {compliance.reviewRequired && (
          <span className="rounded bg-red-50 px-2 py-0.5 text-red-700">Review required</span>
        )}
      </div>

      {violations.length > 0 && (
        <ul className="space-y-1 text-[11px]">
          {violations.slice(0, 6).map((v, idx) => (
            <li key={idx} className="rounded bg-white border border-ink-200 px-2 py-1">
              <div className="flex items-center gap-2">
                <RiskBadge severity={v.severity} />
                <span className="font-medium">{v.rule}</span>
                {v.timestamp && <span className="text-ink-500 text-[10px]">@{v.timestamp}</span>}
                {v.speaker && <span className="text-ink-500 text-[10px]">— {v.speaker}</span>}
              </div>
              {v.evidence && <div className="text-ink-700 italic mt-0.5">"{v.evidence}"</div>}
              {v.notes && <div className="text-ink-600 mt-0.5">{v.notes}</div>}
            </li>
          ))}
        </ul>
      )}

      {/* Legacy v1 fallback (older rows). */}
      {violations.length === 0 && (legacyForbidden.length > 0 || legacyClaims.length > 0) && (
        <ul className="space-y-0.5 text-[11px]">
          {legacyForbidden.map((f, idx) => (
            <li key={`f-${idx}`}>Forbidden phrase: {f.phrase}</li>
          ))}
          {legacyClaims.map((c, idx) => (
            <li key={`c-${idx}`}>Regulated claim: {c.claim}</li>
          ))}
        </ul>
      )}

      {notes.length > 0 && (
        <ul className="space-y-0.5 text-[11px] text-ink-600">
          {notes.slice(0, 4).map((n, idx) => (
            <li key={idx} className="italic">{n}</li>
          ))}
        </ul>
      )}
    </MiniSection>
  );
}
